import React from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import classNames from "classnames";

import { Button } from "../../../components/Button/Button";
import { Card } from "../../../components/Card/Card";
import { Collapse } from "../../../components/Collapse/Collapse";

interface GuidelinesSectionProps {
  isLocked: boolean;
  isOpen: boolean;
  onToggle: () => void;
  isGuidelinesGenerated: boolean;
  guidelineTab: "summary" | "appearance" | "aroma" | "flavor" | "bitterness" | "mouthfeel";
  setGuidelineTab: (tab: "summary" | "appearance" | "aroma" | "flavor" | "bitterness" | "mouthfeel") => void;
}

export const GuidelinesSection: React.FC<GuidelinesSectionProps> = ({
  isLocked,
  isOpen,
  onToggle,
  isGuidelinesGenerated,
  guidelineTab,
  setGuidelineTab,
}) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();

  const guidelines = watch("guidelines") || {};

  return (
    <Collapse
      title={
        <div className="flex justify-between items-center w-full min-w-0 pr-4">
          <div className="flex items-center gap-2">
            {isLocked && <span className="icon-[ph--lock] text-[#877369] text-base" />}
            <span
              className={classNames(
                "text-lg font-bold font-header",
                isLocked ? "text-[#877369]" : "text-[#6c2f00]",
              )}
            >
              {t("Pages:StyleCreator.Guidelines.Title", "3. Style guidelines")}
            </span>
          </div>
          {isGuidelinesGenerated && (
            <span className="px-3 py-1 bg-[#b1f0ce] text-[#0e5138] text-xs font-semibold rounded-full flex items-center gap-1">
              <span className="icon-[ph--check-circle] text-xs" />
              {t(
                "Pages:StyleCreator.Guidelines.AutoGenerated",
                "Auto-generated",
              )}
            </span>
          )}
        </div>
      }
      isOpen={isOpen}
      onToggle={onToggle}
      variant="plain"
      density="comfortable"
      className={classNames(
        "rounded-xl border border-solid transition-all duration-200",
        isLocked ? "opacity-60 cursor-not-allowed" : "",
      )}
      headerClassName={isLocked ? "pointer-events-none" : ""}
    >
      <fieldset disabled={isLocked} className="space-y-6 w-full">
        {/* Guidelines subtabs */}
        <div className="flex border-b border-[#dac2b6] overflow-x-auto gap-2">
          {(
            [
              "summary",
              "appearance",
              "aroma",
              "flavor",
              "bitterness",
              "mouthfeel",
            ] as const
          ).map((tab) => (
            <Button
              key={tab}
              htmlType="button"
              type="base"
              variant="ghost"
              onClick={() => setGuidelineTab(tab)}
              className={classNames(
                "px-4 py-2 border-b-2 font-bold text-xs whitespace-nowrap rounded-none",
                guidelineTab === tab
                  ? "border-[#6c2f00] text-[#6c2f00]"
                  : "border-transparent text-[#54433a] hover:text-[#6c2f00]",
              )}
            >
              {t(
                `Pages:StyleCreator.Guidelines.Tabs.${tab}`,
                tab.charAt(0).toUpperCase() + tab.slice(1),
              )}
            </Button>
          ))}
        </div>

        {/* Guideline content cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {guidelineTab === "summary" && (
            <>
              <Card variant="plain" density="compact" className="bg-white">
                <div className="col-span-12">
                  <div className="text-[10px] font-bold text-[#877369] uppercase tracking-wider mb-2">
                    {t(
                      "Pages:StyleCreator.Guidelines.Overview",
                      "Style overview",
                    )}
                  </div>
                  <p className="text-sm text-[#221a14]">
                    {guidelines.summary ||
                      t(
                        "Pages:StyleCreator.Guidelines.NoOverview",
                        "No overview guidelines generated yet.",
                      )}
                  </p>
                </div>
              </Card>
              <Card variant="plain" density="compact" className="bg-white">
                <div className="col-span-12">
                  <div className="text-[10px] font-bold text-[#877369] uppercase tracking-wider mb-2">
                    {t(
                      "Pages:StyleCreator.Guidelines.Examples",
                      "Commercial examples",
                    )}
                  </div>
                  <p className="text-sm text-[#221a14]">
                    {Array.isArray(guidelines.commercialExamples)
                      ? guidelines.commercialExamples.join(", ")
                      : guidelines.commercialExamples ||
                        t(
                          "Pages:StyleCreator.Guidelines.NoExamples",
                          "No commercial examples generated yet.",
                        )}
                  </p>
                </div>
              </Card>
            </>
          )}

          {guidelineTab === "appearance" && (
            <Card
              variant="plain"
              density="compact"
              className="bg-white col-span-2"
            >
              <div className="col-span-12">
                <div className="text-[10px] font-bold text-[#877369] uppercase tracking-wider mb-2">
                  {t(
                    "Pages:StyleCreator.Guidelines.AppearanceTitle",
                    "Appearance characteristics",
                  )}
                </div>
                <p className="text-sm text-[#221a14]">
                  {guidelines.appearanceText ||
                    t(
                      "Pages:StyleCreator.Guidelines.NoAppearance",
                      "No appearance guidelines generated yet.",
                    )}
                </p>
              </div>
            </Card>
          )}

          {guidelineTab === "aroma" && (
            <Card
              variant="plain"
              density="compact"
              className="bg-white col-span-2"
            >
              <div className="col-span-12">
                <div className="text-[10px] font-bold text-[#877369] uppercase tracking-wider mb-2">
                  {t(
                    "Pages:StyleCreator.Guidelines.AromaTitle",
                    "Aroma characteristics",
                  )}
                </div>
                <p className="text-sm text-[#221a14]">
                  {guidelines.aromaText ||
                    t(
                      "Pages:StyleCreator.Guidelines.NoAroma",
                      "No aroma guidelines generated yet.",
                    )}
                </p>
              </div>
            </Card>
          )}

          {guidelineTab === "flavor" && (
            <Card
              variant="plain"
              density="compact"
              className="bg-white col-span-2"
            >
              <div className="col-span-12">
                <div className="text-[10px] font-bold text-[#877369] uppercase tracking-wider mb-2">
                  {t(
                    "Pages:StyleCreator.Guidelines.FlavorTitle",
                    "Flavor characteristics",
                  )}
                </div>
                <p className="text-sm text-[#221a14]">
                  {guidelines.flavorText ||
                    t(
                      "Pages:StyleCreator.Guidelines.NoFlavor",
                      "No flavor guidelines generated yet.",
                    )}
                </p>
              </div>
            </Card>
          )}

          {guidelineTab === "bitterness" && (
            <Card
              variant="plain"
              density="compact"
              className="bg-white col-span-2"
            >
              <div className="col-span-12">
                <div className="text-[10px] font-bold text-[#877369] uppercase tracking-wider mb-2">
                  {t(
                    "Pages:StyleCreator.Guidelines.BitternessTitle",
                    "Bitterness balance",
                  )}
                </div>
                <p className="text-sm text-[#221a14]">
                  {guidelines.bitternessText ||
                    t(
                      "Pages:StyleCreator.Guidelines.NoBitterness",
                      "No bitterness guidelines generated yet.",
                    )}
                </p>
              </div>
            </Card>
          )}

          {guidelineTab === "mouthfeel" && (
            <Card
              variant="plain"
              density="compact"
              className="bg-white col-span-2"
            >
              <div className="col-span-12">
                <div className="text-[10px] font-bold text-[#877369] uppercase tracking-wider mb-2">
                  {t(
                    "Pages:StyleCreator.Guidelines.MouthfeelTitle",
                    "Mouthfeel characteristics",
                  )}
                </div>
                <p className="text-sm text-[#221a14]">
                  {guidelines.mouthfeelText ||
                    t(
                      "Pages:StyleCreator.Guidelines.NoMouthfeel",
                      "No mouthfeel guidelines generated yet.",
                    )}
                </p>
              </div>
            </Card>
          )}
        </div>
      </fieldset>
    </Collapse>
  );
};
